import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  backend,
  ImportDocumentPropertiesResponse,
  ImportDocumentPropertyError,
  ImportDocumentPropertyRecord,
} from "Backend";
import { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "redux/hooks";
import { fetchForm } from "redux/slices/form.slice";

import { P } from "theme/typography";

interface ImportDocumentPropertiesDialogProps {
  lensId: number;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const renderSuccessTable = (records: ImportDocumentPropertyRecord[]) => {
  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Case ID</TableCell>
              <TableCell>Document ID</TableCell>
              <TableCell>Property Key</TableCell>
              <TableCell>Property Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record, index) => (
              <TableRow key={index}>
                <TableCell>{record.clientCaseId}</TableCell>
                <TableCell>{record.intakeId}</TableCell>
                <TableCell
                  sx={{
                    // monospace
                    fontFamily: "monospace",
                  }}>
                  {record.propertyKey}
                </TableCell>
                <TableCell>{record.propertyValue}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const renderErrorTable = (errors: ImportDocumentPropertyError[], tryAgain: () => void) => {
  return (
    <Box
      sx={{
        gap: 2,
        display: "flex",
        flexDirection: "column",
      }}>
      <Box>
        <Alert
          severity="error"
          action={
            <Button
              sx={{
                mt: 0,
              }}
              color="inherit"
              onClick={tryAgain}>
              Try Again
            </Button>
          }>
          Please fix the errors and try again.
        </Alert>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>Error(s)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.map((error, index) => (
              <TableRow key={index}>
                <TableCell>{error.rowNumber + 1}</TableCell>
                <TableCell>{error.errors.join(", ")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const ImportDocumentPropertiesDialog: FC<ImportDocumentPropertiesDialogProps> = (props) => {
  const { lensId, setOpen } = props;
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | undefined>();
  const [importResponse, setImportResponse] = useState<ImportDocumentPropertiesResponse | undefined>();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  useEffect(() => {
    void dispatch(fetchForm({ lensId }));
  }, [lensId]);

  const onFile = (evt: ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];

    if (file) {
      setImportResponse(undefined);
      setFile(file);
      backend
        .importDocumentPropertiesFromExcel(file, lensId, true)
        .then((response) => {
          setImportResponse(response);
        })
        .catch((error) => {
          console.error("Failed to import document properties: ", error);
          enqueueSnackbar("Unable to Import", { variant: "error" });
        });
    }
  };

  const resetFile = () => {
    if (fileInput.current) {
      fileInput.current.value = "";
    }
    setFile(undefined);
    setImportResponse(undefined);
  };

  const onImport = () => {
    if (file) {
      setLoading(true);
      backend
        .importDocumentPropertiesFromExcel(file, lensId, false)
        .then((res) => {
          enqueueSnackbar(`${res.records && res.records.length} Records Imported`, { variant: "success" });
        })
        .catch(() => {
          enqueueSnackbar("Unable to Import", { variant: "error" });
        })
        .finally(() => {
          resetFile();
          setLoading(false);
          setOpen(false);
        });
    }
  };

  return (
    <Dialog open={true} onClose={() => setOpen(false)} maxWidth="sm" fullWidth={true}>
      <DialogTitle>Import Document Properties</DialogTitle>
      <DialogContent>
        {/* Instructions, including template download url */}
        <Box>
          <P variant="body2">
            To import document properties, first{" "}
            <Link href="/Document_Property_Template.xlsx" target="_blank">
              download the template file.
            </Link>{" "}
            Then, fill in the document properties for each case. Finally, upload the file below.
          </P>
          {/* File select input */}
          <Box sx={{ marginTop: 2 }}>
            <input
              type="file"
              ref={fileInput}
              autoComplete="off"
              tabIndex={-1}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "none" }}
              onChange={onFile}
            />
            {!file && (
              <Button
                id="import_document_properties_file"
                component="label"
                color="default"
                variant="outlined"
                onClick={() => {
                  if (fileInput.current) {
                    fileInput.current.click();
                  }
                }}>
                Choose File
              </Button>
            )}
            {/* Alert row blue with file name and delete button if response records and no errors */}
            {file && importResponse && !importResponse.errors && (
              <Alert
                severity="info"
                action={
                  <Button
                    sx={{
                      mt: 0,
                      pt: 0,
                    }}
                    color="inherit"
                    onClick={() => {
                      // reset the file in the ref so that the user can upload the same file again
                      if (fileInput.current) {
                        fileInput.current.value = "";
                      }
                      // reset the file in state
                      setFile(undefined);
                      setImportResponse(undefined);
                    }}>
                    Remove
                  </Button>
                }>
                {file.name}
              </Alert>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            mt: 2,
          }}>
          {importResponse && importResponse.errors && renderErrorTable(importResponse.errors, resetFile)}
          {importResponse && importResponse.records && renderSuccessTable(importResponse.records)}
          {/* If file is good but no records, show an alert */}
          {file && importResponse && !importResponse.errors && !importResponse.records && (
            <Alert severity="warning">
              <AlertTitle>There is a problem with the file uploaded.</AlertTitle>
              We found no records in the file. Please check your file and try again.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          color="default"
          onClick={() => {
            resetFile();
            setOpen(false);
          }}
          sx={{ marginRight: 1 }}>
          Cancel
        </Button>
        <LoadingButton
          id="import_document_properties_submit"
          color="default"
          variant="contained"
          onClick={onImport}
          loading={isLoading}
          disabled={!importResponse || !importResponse.records}>
          Import
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDocumentPropertiesDialog;
